import { Injectable } from '@angular/core';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, throwError } from 'rxjs';
import {
  AsifPipelineStatus,
  Project,
  ProjectFilterResponse,
  ProjectMetaData,
  ProjectsResponse,
  UserInfo,
} from './projects-api-models';
import { Router } from '@angular/router';
import { catchError, map } from 'rxjs/operators';

export type Operation =
  | '==='
  | '!=='
  | '>'
  | '<'
  | '<='
  | '>='
  | 'includes'
  | 'hasHashtag'
  | 'is_archived'
  | 'level'
  | 'date<'
  | 'date>'
  | 'date<='
  | 'date>='
  | 'date==='
  | 'date!==';

export interface FilterItem {
  key: string;
  value: any;
}

/** The only `filter_type` values `get_all_projects` accepts; anything else is rejected with 400. */
export type ProjectFilterType =
  | 'all_projects'
  | 'my_projects'
  | 'my_team'
  | 'shared_with_me'
  | 'my_organization';

/** The only `sort` values `get_all_projects` accepts. */
export type ProjectSortType = 'newest' | 'oldest' | 'last_report' | 'alpha_asc' | 'alpha_desc';

// Declared as a type alias rather than an interface so it keeps an implicit index signature and
// can still be handed to `ProjectFilterService.setFilters`.
export type ProjectsQueryOptions = {
  label_ids?: string[];
  project_name?: string;
  parent_project_id?: string;
  include_sub_projects?: 0 | 1 | boolean;
  asif_pipeline_status?: AsifPipelineStatus;
  project_manager?: string;
  organization?: string;
  creator?: string;
  creation_date_from?: string;
  creation_date_to?: string;
  is_archived?: 0 | 1 | boolean;
  is_cloned?: 0 | 1;
  /** Sent to the API as a comma-separated string; the backend matches them with OR. */
  hashtags?: string[];
  sort?: ProjectSortType;
  filter_type?: ProjectFilterType;
  // NWC-specific filters
  search?: string;
  project_number?: string;
  p6_project_id?: string;
  ebs_po_number?: string;
};

@Injectable({
  providedIn: 'root',
})
export class Projects2Service {
  filteredProjects: any[] = [];
  allProjects: any[] = [];
  totalFilteredProjects: number = 0;
  totalAllProjects: number = 0;
  areFilteredProjectsLoading: boolean = false;
  loadingNewPage: boolean = false;
  totalProjects: number = 0;
  areAllProjectsLoading: boolean = false;
  projectsMetadata: ProjectMetaData = {
    totalProject: 0,
    allProjectCount: 0,
    myProjectsCount: 0,
    myTeamCount: 0,
    sharedWithMeCount: 0,
    projectManagers: [],
    projectCreators: [],
    projectTypes: [],
    organizations: [],
    tags: [],
  };
  addParentProjectsList: Project[];
  private options: ProjectsQueryOptions;
  currentPage: number;

  constructor(
    private endPoint: RestfulEndpoint,
    private _toaster: ToastrService,
    private _router: Router,
    private translateService: TranslocoService,
  ) {}

  isProjectLevelSelection(projectIds: string[]): boolean {
    if (projectIds.length === 0) {
      return false;
    }

    const projectsById = new Map(this.allProjects.map((project) => [project.id, project]));

    return projectIds.every((projectId) => {
      const project = projectsById.get(projectId);
      const level = Number(project?.level);

      return Number.isFinite(level) && level >= 3;
    });
  }

  async fetchAllProjects(pageNo?: number, project_name?: string, search?: string) {
    const itemPerPage = 20;
    const fetchProjectPage = async (page: number) => {
      this.areAllProjectsLoading = true;
      try {
        const projectsResponse = await this.api_getAllProjects(page, itemPerPage, {
          include_sub_projects: true,
          project_name: project_name,
          search,
          is_archived: 0,
          filter_type: 'my_organization',
        }).toPromise();

        if (!projectsResponse) {
          throw new Error('No projects found');
        }

        this.totalAllProjects = projectsResponse.total;
        if (pageNo == 1) {
          this.allProjects = [];
        }
        this.allProjects.push(...projectsResponse.projects);
        // save only unique projects no duplicate
        this.allProjects = this.allProjects.filter(
          (value, index, self) => index === self.findIndex((v) => v.id === value.id),
        );

        this.addParentProjectsList = this.allProjects.filter((p) => {
          return p.level === 1 && p.children_count === 0;
        });

        this.areAllProjectsLoading = false;
      } catch (error) {
        this.areAllProjectsLoading = false;
        this.errorHandlingV1(error);
      }
    };

    await fetchProjectPage(pageNo);
  }

  async fetchAllProjectsForAction(
    pageNo?: number,
    project_name?: string,
    projectId?: string,
    itemsPerPage: number = 20,
    includeSubProjects?: boolean,
    search?: string,
  ) {
    const itemPerPage = itemsPerPage;
    const fetchProjectPage = async (page: number) => {
      this.areAllProjectsLoading = true;
      try {
        const projectsResponse = await this.api_getAllProjects(page, itemPerPage, {
          include_sub_projects:
            includeSubProjects !== undefined
              ? includeSubProjects
              : project_name
              ? true
              : !!projectId,
          project_name: project_name,
          search,
          is_archived: 0,
          filter_type: 'my_organization',
          parent_project_id: projectId || undefined,
        }).toPromise();

        if (!projectsResponse) {
          throw new Error('No projects found');
        }

        if (projectId) {
          this.areAllProjectsLoading = false;
          return projectsResponse.projects;
        }

        this.totalAllProjects = projectsResponse.total;
        this.currentPage = pageNo;
        if (pageNo == 1) {
          this.allProjects = [];
        }
        this.allProjects.push(...projectsResponse.projects);
        // save only unique projects no duplicate
        this.allProjects = this.allProjects.filter(
          (value, index, self) => index === self.findIndex((v) => v.id === value.id),
        );

        this.addParentProjectsList = this.allProjects.filter((p) => {
          return p.level === 1 && p.children_count === 0;
        });

        this.areAllProjectsLoading = false;
      } catch (error) {
        this.areAllProjectsLoading = false;
        this.errorHandlingV1(error);
      }
    };

    return await fetchProjectPage(pageNo);
  }

  async fetchProjects(options?: ProjectsQueryOptions): Promise<void> {
    this.areFilteredProjectsLoading = true;
    this.filteredProjects = [];
    this.allProjects = [];

    this.projectsMetadata.totalProject = this.totalFilteredProjects;

    this.options = options;

    this.prepareProjectMeta().subscribe(
      () => {
        // Success: Metadata preparation completed
        // You can perform further actions here, such as updating UI or triggering other logic
      },
      (error) => {
        // Error: Metadata preparation failed
        console.error('Metadata preparation failed:', error);
        // You can handle the error here, such as displaying an error message or taking alternative actions
      },
    );

    await this.loadNextProjects(1);

    this.areFilteredProjectsLoading = false;
  }

  async loadNextProjects(page: number) {
    this.loadingNewPage = true;
    const projectsResponse = await this.api_getAllProjects(page, 20, this.options)
      .toPromise()
      .catch((r) => this.errorHandling(r));

    if (!projectsResponse) {
      return;
    }
    this.totalProjects = projectsResponse.total_project;
    this.totalFilteredProjects = projectsResponse.total;
    if (page == 1) {
      this.filteredProjects = [];
    }

    // Sanitize projects data to handle backend inconsistencies
    projectsResponse.projects.forEach((project) => {
      // Ensure reports_count is a number to prevent string/number type issues
      if (project.reports_count !== undefined && project.reports_count !== null) {
        project.reports_count = Number(project.reports_count);
      } else {
        project.reports_count = 0;
      }

      // Workaround for backend issue where reports_count might be missing/zero when sorting by last_report
      // If we have a valid last_report_date, we should assume there is at least one report
      if (
        !project.reports_count &&
        project.last_report_date &&
        project.last_report_date !== 'N/A' &&
        project.last_report_date !== ''
      ) {
        project.reports_count = 1;
      }
    });

    this.filteredProjects.push(...projectsResponse.projects);
    this.loadingNewPage = false;
  }

  async addTagsToProjects(project_ids, tags, exclude_ids): Promise<boolean> {
    const response = await this.api_addTagsToProject({
      project_ids,
      tags,
      exclude_ids,
    }).toPromise();
    if (response.success || response.status == 'success') {
      return true;
    } else {
      return false;
    }
  }

  async addMembersToProjects(project_ids, usersInfo, exclude_ids): Promise<boolean> {
    try {
      const response = await this.api_addMembersToProject({
        project_ids,
        usersInfo,
        exclude_ids,
      }).toPromise();

      if (response.Status || response.status == 'success') {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      this._toaster.error(error.error.error);
      return false;
    }
  }

  async addProjectsToArchive(project_ids, exclude_ids?): Promise<boolean> {
    const response = await this.api_addProjectsToArchive({
      project_ids,
      exclude_ids,
    }).toPromise();
    if (response.success || response.status == 'success') {
      return true;
    } else {
      return false;
    }
  }

  async removeProjectsFromArchive(project_ids): Promise<boolean> {
    const response = await this.api_removeProjectsFromArchive({
      project_ids,
    }).toPromise();
    if (response.success) {
      await this.fetchProjects();
      return true;
    } else {
      return false;
    }
  }

  async deleteProject(project_id): Promise<boolean> {
    const response = await this.api_deleteProject(project_id).toPromise();
    return response.success;
  }

  async cloneProjects(project_id: string, copies: number): Promise<boolean> {
    try {
      const response = await this.api_cloneProject({
        project_id,
        copies,
      }).toPromise();
      return response.status;
    } catch (error) {
      const errorMessage =
        error || this.translateService.translate('Something went wrong, please try again');
      this._toaster.error(errorMessage);
      return false;
    }
  }

  // api
  api_getAllProjects(
    page: number = 1,
    per_page: number = 50,
    options?: ProjectsQueryOptions,
  ): Observable<ProjectsResponse> {
    let body = {
      per_page,
      page,
      ...(options && {
        label_ids: options.label_ids,
        project_name: options.project_name,
        asif_pipeline_status: options.asif_pipeline_status,
        parent_project_id: options.parent_project_id,
        include_sub_projects: options.include_sub_projects,
        project_manager: options.project_manager,
        organization: options.organization,
        creator: options.creator,
        creation_date_from: options.creation_date_from,
        creation_date_to: options.creation_date_to,
        is_archived: options.is_archived,
        is_cloned: options.is_cloned,
        sort: options.sort,
        hashtags: options.hashtags?.length ? options.hashtags.join(',') : undefined,
        filter_type: options.filter_type,
        // NWC-specific filters
        search: options.search,
        project_number: options.project_number,
        p6_project_id: options.p6_project_id,
        ebs_po_number: options.ebs_po_number,
      }),
      include_percentage: true,
    };

    return this.endPoint.post('get_all_projects', body);
  }

  public prepareProjectMeta(projectId?: string): Observable<void> {
    return this.api_getInitFilter(projectId).pipe(
      catchError((error) => {
        return throwError(error); // Rethrow the error to propagate it to the caller
      }),
      map((response: ProjectFilterResponse) => {
        this.projectsMetadata.projectManagers = response.project_managers;
        this.projectsMetadata.tags = response.hashtags;
        this.projectsMetadata.organizations = response.organizations;
        this.projectsMetadata.projectCreators = response.project_creators;
      }),
    );
  }

  private api_getInitFilter(projectId?: string): Observable<ProjectFilterResponse> {
    const payload = projectId ? { project_id: projectId } : null;
    return this.endPoint.post('get-projects-filter-intital-data', payload);
  }

  private api_cloneProject(body: {
    project_id: string;
    copies: number;
  }): Observable<{ status: boolean }> {
    return this.endPoint.post('clone_project', body).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'An unknown error occurred';
        return throwError(errorMessage);
      }),
    );
  }

  private api_addTagsToProject(body: {
    project_ids: string[] | '*';
    tags: string;
    exclude_ids: string[];
  }): Observable<{ success: boolean; status: string }> {
    return this.endPoint.post('add_tags_to_projects', body);
  }

  private api_addMembersToProject(body: {
    project_ids: string[] | '*';
    usersInfo: UserInfo[];
    exclude_ids: string[];
  }): Observable<{ Status: boolean; status: string }> {
    return this.endPoint.post('share_projects', body);
  }

  private api_deleteProject(id: any): Observable<{ success: boolean }> {
    return this.endPoint.post('delete_project', { id });
  }

  // ErrorHandling
  private errorHandling(reason): void {
    if (reason.error && reason.error.error) {
      this._toaster.error(this.translateService.translate(reason.error.error.join(',')));
    } else {
      this._toaster.error(
        this.translateService.translate('Something went wrong, please try again later.'),
      );
      this._router.navigate(['500']);
    }
    // throw new Error(reason);
  }

  private errorHandlingV1(reason): void {
    if (reason.error && reason.error.errors && reason.error.errors.length > 0) {
      const errorMessage = reason.error.errors.join(', ');
      // Handle specific error for project name validation
      this._toaster.error(this.translateService.translate(errorMessage));
    } else {
      this._toaster.error(
        this.translateService.translate('Something went wrong, please try again later.'),
      );
      this._router.navigate(['500']); // Only go to 500 page for unexpected errors
    }
  }

  private api_addProjectsToArchive(body: {
    project_ids: string[] | '*';
    exclude_ids: string[];
  }): Observable<{ success: boolean; status: string }> {
    return this.endPoint.post('archive_projects', body);
  }

  private api_removeProjectsFromArchive(body: {
    project_ids: string[];
  }): Observable<{ success: boolean }> {
    return this.endPoint.post('unarchive_projects', body);
  }
}
